/**
 * oddit-section-main-product.js
 * Handles tab navigation, reviews carousel with swipe + autoplay,
 * and buy box option toggling.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-main-product';
  var sectionState = new Map();

  function initSection(section) {
    var state = {
      autoplay: null,
      scrollHandler: null
    };

    state.scrollSpy = initTabScrollSpy(section);
    initTabs(section, state.scrollSpy);
    initBuyBoxOptions(section);
    state.autoplay = initReviewsCarousel(section, state);
    state.galleryModal = initGalleryModal(section);
    initDetailToggle(section);

    sectionState.set(section.id || section, state);
  }

  function destroySection(section) {
    var key = section.id || section;
    var state = sectionState.get(key);
    if (!state) return;
    if (state.autoplay) state.autoplay.destroy();
    if (state.galleryModal) state.galleryModal.destroy();
    if (state.scrollSpy) state.scrollSpy.destroy();
    sectionState.delete(key);
  }

  /* ---------- Expandable Content Toggle (Show less / more) ---------- */
  function initDetailToggle(section) {
    var btn = section.querySelector('[data-detail-toggle]');
    var target = section.querySelector('[data-expandable]');
    if (!btn || !target) return;

    var label = btn.querySelector('.oddit-section-main-product__show-less-label');

    btn.addEventListener('click', function () {
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      var next = !expanded;
      btn.setAttribute('aria-expanded', next ? 'true' : 'false');
      target.classList.toggle('oddit-section-main-product__expandable--collapsed', !next);
      if (label) label.textContent = next ? 'Show less' : 'Show more';
    });
  }

  /* ---------- Tab Scroll Spy ---------- */
  function initTabScrollSpy(section) {
    var tabNav = section.querySelector('.oddit-section-main-product__tabs');
    if (!tabNav || typeof IntersectionObserver === 'undefined') return null;

    var tabs = tabNav.querySelectorAll('.oddit-section-main-product__tab');
    var idToTab = {};
    var targets = [];
    for (var i = 0; i < tabs.length; i++) {
      var id = tabs[i].getAttribute('data-tab');
      var el = id ? section.querySelector('#' + id) : null;
      if (el) {
        idToTab[id] = tabs[i];
        targets.push(el);
      }
    }
    if (!targets.length) return null;

    var visible = {};
    var suspended = false;
    var suspendTimer = null;

    function setActive(id) {
      for (var i = 0; i < tabs.length; i++) {
        var isActive = tabs[i].getAttribute('data-tab') === id;
        tabs[i].classList.toggle('oddit-section-main-product__tab--active', isActive);
        tabs[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
      }
    }

    function pickTopMost() {
      if (suspended) return;
      var topMost = null;
      var topMostY = Infinity;
      for (var id in visible) {
        if (!visible[id]) continue;
        var rect = visible[id].getBoundingClientRect();
        if (rect.top < topMostY) {
          topMostY = rect.top;
          topMost = id;
        }
      }
      if (topMost) setActive(topMost);
    }

    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        var id = entry.target.id;
        visible[id] = entry.isIntersecting ? entry.target : null;
      }
      pickTopMost();
    }, {
      rootMargin: '-80px 0px -55% 0px',
      threshold: 0
    });

    for (var j = 0; j < targets.length; j++) {
      observer.observe(targets[j]);
    }

    return {
      destroy: function () {
        observer.disconnect();
        if (suspendTimer) clearTimeout(suspendTimer);
      },
      suspend: function (ms) {
        suspended = true;
        if (suspendTimer) clearTimeout(suspendTimer);
        suspendTimer = setTimeout(function () {
          suspended = false;
          suspendTimer = null;
          pickTopMost();
        }, ms || 900);
      }
    };
  }

  /* ---------- Photo Tour Modal ---------- */
  function initGalleryModal(section) {
    var modal = section.querySelector('[data-gallery-modal]');
    var openBtn = section.querySelector('[data-gallery-open]');
    var closeBtn = modal ? modal.querySelector('[data-gallery-close]') : null;
    if (!modal || !openBtn || !closeBtn) return null;

    var lastFocused = null;
    var prevBodyOverflow = '';

    function getFocusable() {
      return modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
    }

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      var focusable = getFocusable();
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function handleKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      } else {
        trapFocus(e);
      }
    }

    function open() {
      lastFocused = document.activeElement;
      modal.hidden = false;
      // Force reflow so the transition runs from the hidden state.
      void modal.offsetWidth;
      modal.classList.add('oddit-section-main-product__gallery-modal--open');
      modal.setAttribute('aria-hidden', 'false');
      prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKey);
      // Defer focus so it lands after the transition starts.
      window.setTimeout(function () { closeBtn.focus(); }, 50);
    }

    function close() {
      modal.classList.remove('oddit-section-main-product__gallery-modal--open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = prevBodyOverflow;
      document.removeEventListener('keydown', handleKey);

      var onTransitionEnd = function (e) {
        if (e.target !== modal) return;
        modal.hidden = true;
        modal.removeEventListener('transitionend', onTransitionEnd);
      };
      modal.addEventListener('transitionend', onTransitionEnd);

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }

    openBtn.addEventListener('click', open);
    closeBtn.addEventListener('click', close);

    return {
      destroy: function () {
        openBtn.removeEventListener('click', open);
        closeBtn.removeEventListener('click', close);
        document.removeEventListener('keydown', handleKey);
        if (document.body.style.overflow === 'hidden') {
          document.body.style.overflow = prevBodyOverflow;
        }
      }
    };
  }

  /* ---------- Tab Navigation ---------- */
  function initTabs(section, scrollSpy) {
    var tabNav = section.querySelector('.oddit-section-main-product__tabs');
    if (!tabNav) return;

    tabNav.addEventListener('click', function (e) {
      var btn = e.target.closest('.oddit-section-main-product__tab');
      if (!btn) return;

      var tabs = tabNav.querySelectorAll('.oddit-section-main-product__tab');
      for (var i = 0; i < tabs.length; i++) {
        tabs[i].classList.remove('oddit-section-main-product__tab--active');
        tabs[i].setAttribute('aria-selected', 'false');
      }
      btn.classList.add('oddit-section-main-product__tab--active');
      btn.setAttribute('aria-selected', 'true');

      if (scrollSpy && scrollSpy.suspend) scrollSpy.suspend();

      var targetId = btn.getAttribute('data-tab');
      var target = section.querySelector('#' + targetId);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  /* ---------- Buy Box Option Buttons ---------- */
  function initBuyBoxOptions(section) {
    var buyBoxes = section.querySelectorAll('.oddit-section-main-product__buy-box');
    for (var i = 0; i < buyBoxes.length; i++) {
      initBuyBox(buyBoxes[i]);
    }
  }

  function initBuyBox(buyBox) {
    var optionSections = buyBox.querySelectorAll('.oddit-section-main-product__buy-box-options');
    if (!optionSections.length) return;

    var variantsEl = buyBox.querySelector('[data-product-variants-json]');
    var variants = [];
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent); } catch (e) { variants = []; }
    }

    var priceEl = buyBox.querySelector('[data-product-price]');
    var compareEl = buyBox.querySelector('[data-product-price-compare]');
    var fromEl = buyBox.querySelector('[data-product-price-label]');
    var cartBtn = buyBox.querySelector('[data-add-to-cart]');
    var totalOptions = optionSections.length;
    var selected = {};
    var optionGroups = buyBox.querySelectorAll('.oddit-section-main-product__buy-box-option-btns');

    for (var g = 0; g < optionGroups.length; g++) {
      (function (group) {
        group.addEventListener('click', function (e) {
          var btn = e.target.closest('.oddit-section-main-product__buy-box-option-btn');
          if (!btn || btn.disabled) return;
          selectOption(group, btn);
        });
      })(optionGroups[g]);
    }

    if (cartBtn) {
      cartBtn.addEventListener('click', function () {
        if (cartBtn.disabled) return;
        var variantId = Number(cartBtn.getAttribute('data-variant-id'));
        if (!variantId) return;
        // Seed the cart drawer with this variant's compare-at price so it can
        // render strike-through pricing (cart.js does not expose compare_at_price).
        if (window.OdditCartDrawer && window.OdditCartDrawer.setCompareAt) {
          for (var vi = 0; vi < variants.length; vi++) {
            if (Number(variants[vi].id) === variantId) {
              window.OdditCartDrawer.setCompareAt(variantId, variants[vi].compare_at_price || 0);
              break;
            }
          }
        }
        cartBtn.disabled = true;
        var addPromise = (window.OdditCartDrawer && window.OdditCartDrawer.addToCart)
          ? window.OdditCartDrawer.addToCart(variantId)
          : fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            });
        Promise.resolve(addPromise)
          .then(function () {
            if (window.OdditCartDrawer && window.OdditCartDrawer.open) {
              window.OdditCartDrawer.open();
            }
          })
          .catch(function (err) {
            console.error('[OdditMainProduct] addToCart error:', err);
          })
          .finally(function () {
            cartBtn.disabled = false;
          });
      });
    }

    // Pre-select from ?variant= query param on load
    applyVariantFromQuery();

    // Reflect the initial state so the "from" price + compare-at strike show
    // from the start (before any option is selected).
    updatePriceAndCart();

    function selectOption(group, btn) {
      var btns = group.querySelectorAll('.oddit-section-main-product__buy-box-option-btn');
      for (var i = 0; i < btns.length; i++) {
        btns[i].classList.remove('oddit-section-main-product__buy-box-option-btn--active');
      }
      btn.classList.add('oddit-section-main-product__buy-box-option-btn--active');

      var position = parseInt(btn.getAttribute('data-option-position'), 10);
      var value = btn.getAttribute('data-option-value');
      selected[position] = value;

      var labelSpan = buyBox.querySelector('[data-selected-option="' + position + '"]');
      if (labelSpan) labelSpan.textContent = value;

      updatePriceAndCart();
    }

    function applyVariantFromQuery() {
      var params = new URLSearchParams(window.location.search);
      var id = params.get('variant');
      if (!id) return;
      var variant = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(id)) { variant = variants[i]; break; }
      }
      if (!variant) return;

      for (var g = 0; g < optionGroups.length; g++) {
        var group = optionGroups[g];
        var btns = group.querySelectorAll('.oddit-section-main-product__buy-box-option-btn');
        for (var b = 0; b < btns.length; b++) {
          var btn = btns[b];
          var position = parseInt(btn.getAttribute('data-option-position'), 10);
          var value = btn.getAttribute('data-option-value');
          if (variant.options[position - 1] === value) {
            selectOption(group, btn);
            break;
          }
        }
      }
    }

    function updateVariantQueryParam(id) {
      if (!window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      if (id) {
        url.searchParams.set('variant', id);
      } else {
        url.searchParams.delete('variant');
      }
      window.history.replaceState({}, '', url.toString());
    }

    // Show/hide the strike-through compare-at price for a given variant.
    function setCompareFor(variant) {
      if (!compareEl) return;
      if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.innerHTML = variant.compare_at_price_formatted;
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    function updatePriceAndCart() {
      if (!variants.length) return;

      var matching = variants.filter(function (v) {
        for (var pos in selected) {
          if (!Object.prototype.hasOwnProperty.call(selected, pos)) continue;
          if (v.options[parseInt(pos, 10) - 1] !== selected[pos]) return false;
        }
        return true;
      });

      if (!matching.length) return;

      var selectedCount = Object.keys(selected).length;
      var fullMatch = selectedCount === totalOptions && matching.length === 1 ? matching[0] : null;

      if (fullMatch) {
        if (fromEl) fromEl.hidden = true;
        if (priceEl) priceEl.innerHTML = fullMatch.price_formatted;
        setCompareFor(fullMatch);
        if (cartBtn) {
          cartBtn.setAttribute('data-variant-id', fullMatch.id);
          cartBtn.disabled = !fullMatch.available;
          var defaultText = cartBtn.getAttribute('data-default-text') || 'Add To Cart';
          var soldOutText = cartBtn.getAttribute('data-sold-out-text') || 'Sold Out';
          cartBtn.textContent = fullMatch.available ? defaultText : soldOutText;
        }
        updateVariantQueryParam(fullMatch.id);
      } else {
        if (fromEl) fromEl.hidden = false;
        var cheapest = matching.reduce(function (a, b) { return a.price <= b.price ? a : b; });
        if (priceEl) priceEl.innerHTML = cheapest.price_formatted;
        setCompareFor(cheapest);
        if (cartBtn) {
          cartBtn.setAttribute('data-variant-id', '');
          cartBtn.disabled = true;
          var pendingText = cartBtn.getAttribute('data-pending-text') || 'Please select your options';
          cartBtn.textContent = pendingText;
        }
        updateVariantQueryParam(null);
      }
    }
  }

  /* ---------- Reviews Carousel ---------- */
  function initReviewsCarousel(section, state) {
    var carousel = section.querySelector('.oddit-section-main-product__reviews-carousel');
    if (!carousel) return null;

    var track = carousel.querySelector('.oddit-section-main-product__reviews-track');
    var dots = carousel.querySelectorAll('.oddit-section-main-product__reviews-dot');
    if (!track) return null;

    var startX = 0;

    // Swipe support
    track.addEventListener('touchstart', function (e) {
      startX = e.touches[0].pageX;
    }, { passive: true });

    track.addEventListener('touchend', function (e) {
      var deltaX = e.changedTouches[0].pageX - startX;
      if (Math.abs(deltaX) > 50) {
        var currentIndex = getCurrentSlideIndex(track);
        var direction = deltaX > 0 ? -1 : 1;
        scrollToSlide(track, currentIndex + direction);
      }
    }, { passive: true });

    // Prev / Next arrows
    var prevBtn = carousel.querySelector('[data-reviews-prev]');
    var nextBtn = carousel.querySelector('[data-reviews-next]');

    function updateArrows(index) {
      var total = track.children.length;
      if (prevBtn) prevBtn.hidden = index <= 0;
      if (nextBtn) nextBtn.hidden = index >= total - 1;
    }

    // Sync dots + arrows on scroll
    track.addEventListener('scroll', function () {
      var index = getCurrentSlideIndex(track);
      updateDots(dots, index);
      updateArrows(index);
    }, { passive: true });

    // Dot click
    for (var d = 0; d < dots.length; d++) {
      (function (idx) {
        dots[idx].addEventListener('click', function () {
          scrollToSlide(track, idx);
          if (autoplayInstance) {
            autoplayInstance.stop();
            autoplayInstance.start();
          }
        });
      })(d);
    }

    // Arrow clicks
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        var idx = getCurrentSlideIndex(track);
        scrollToSlide(track, idx - 1);
        if (autoplayInstance) { autoplayInstance.stop(); autoplayInstance.start(); }
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        var idx = getCurrentSlideIndex(track);
        scrollToSlide(track, idx + 1);
        if (autoplayInstance) { autoplayInstance.stop(); autoplayInstance.start(); }
      });
    }

    // Initial arrow state
    updateArrows(getCurrentSlideIndex(track));

    // Autoplay
    var autoplayEnabled = carousel.getAttribute('data-autoplay') === 'true';
    var autoplayInstance = null;

    if (autoplayEnabled) {
      autoplayInstance = initAutoplay(carousel, track, dots);
    }

    return autoplayInstance;
  }

  function getCurrentSlideIndex(track) {
    var slides = track.children;
    if (!slides.length) return 0;
    var slideWidth = slides[0].offsetWidth + 10; // gap
    return Math.round(track.scrollLeft / slideWidth);
  }

  function scrollToSlide(track, index) {
    var slides = track.children;
    var max = slides.length - 1;
    index = Math.max(0, Math.min(index, max));
    var slideWidth = slides[0].offsetWidth + 10;
    track.scrollTo({ left: index * slideWidth, behavior: 'smooth' });
  }

  function updateDots(dots, activeIndex) {
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('oddit-section-main-product__reviews-dot--active', i === activeIndex);
    }
  }

  /* ---------- Autoplay (RAF-based) ---------- */
  function initAutoplay(carousel, track, dots) {
    var speedSeconds = parseInt(carousel.getAttribute('data-autoplay-speed'), 10) || 5;
    var intervalMs = speedSeconds * 1000;

    var rafId = null;
    var lastAdvance = performance.now();

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) {
      return { start: function () {}, stop: function () {}, destroy: function () {} };
    }

    function tick(now) {
      if (now - lastAdvance >= intervalMs) {
        var idx = getCurrentSlideIndex(track);
        var total = track.children.length;
        var next = (idx + 1) % total;
        scrollToSlide(track, next);
        updateDots(dots, next);
        lastAdvance = now;
      }
      rafId = requestAnimationFrame(tick);
    }

    function start() {
      if (rafId) return;
      lastAdvance = performance.now();
      rafId = requestAnimationFrame(tick);
    }

    function stop() {
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    function destroy() {
      stop();
    }

    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });

    if (track) {
      track.addEventListener('mouseenter', stop);
      track.addEventListener('mouseleave', start);
      track.addEventListener('focusin', stop);
      track.addEventListener('focusout', start);
      track.addEventListener('touchstart', function () { stop(); }, { passive: true });
      track.addEventListener('touchend', function () {
        lastAdvance = performance.now();
        start();
      }, { passive: true });
    }

    reducedMotion.addEventListener('change', function (e) {
      e.matches ? stop() : start();
    });

    start();

    return { start: start, stop: stop, destroy: destroy };
  }

  /* ---------- Init ---------- */
  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();